import moment from 'moment'
import React, { ReactNode, useState, CSSProperties } from 'react'
import { Button, Modal, StopMarker, StopsRouteDots } from '../..'
import { images } from '../../../images'
import { IStop } from '../../../models'
import { LiteralsService } from '../../../services'
import theme from '../../../theme'
import StyledSection from './styles'
import { indicativeTimeMark } from '../../../helpers/utils'

interface IProps {
  aprox?: boolean
  reservation?: any
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
}

export const Stops: React.FC<IProps> = ({ aprox, reservation, pickedOriginBusStop, pickedDestinationBusStop }) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const toggleModalVisibility = (): void => setIsModalVisible(current => !current)

  const isBeginingStopUnavailable = (): boolean => {
    const isBeginingStopDisabled = pickedOriginBusStop
      ? reservation?.beginningStop.name !== pickedOriginBusStop.name
      : false

    return !!reservation?.alternative && isBeginingStopDisabled
  }

  const isDestinationStopUnavailable = (): boolean => {
    const isDestinationStopDisabled = pickedDestinationBusStop
      ? reservation?.seats[0].exitStop.name !== pickedDestinationBusStop.name
      : false

    return !!reservation?.alternative && isDestinationStopDisabled
  }

  const stopDisabledOrigin = (): ReactNode => {
    if (!isBeginingStopUnavailable()) return null
    return (
      <>
        <StopMarker color={ theme.colors.greyMedium } />
        <div className="dashed">...</div>
      </>
    )
  }

  const stopDisabledDestination = (): ReactNode => {
    if (!isDestinationStopUnavailable()) return null
    return (
      <>
        <div className="dashed">...</div>
        <StopMarker color={ theme.colors.greyMedium } />
      </>
    )
  }

  const walkingAlternativeInStop = (): ReactNode => {
    if (!isBeginingStopUnavailable() || !pickedOriginBusStop) return null
    return (
      <div className="stop-info">
        <div className='row-details'>
          <p className="text-bottom not-available">
            { pickedOriginBusStop.name }
          </p>
          <p className="text-top not-available">
            ({ LiteralsService.get('notAvailable') })
          </p>
          <div>
            <button onClick={ toggleModalVisibility }>
              <img src={ images['information'] } alt="info" />
            </button>
          </div>
        </div>
        <div className="row-details">
          <img src={ images['pedestrian'] } alt="Pedestrian" />
          <span className="text-top">
            { LiteralsService.get('walk', true) }
          </span>
        </div>
      </div>
    )
  }

  const walkingAlternativeOutStop = (): ReactNode => {
    if (!isDestinationStopUnavailable() || !pickedDestinationBusStop) return null
    return (
      <div className="stop-info">
        <div className="row-details" style={ { marginBottom: 5 } }>
          <img src={ images['pedestrian'] } alt="Pedestrian" />
          <span className="text-top" style={ { marginLeft: 8 } }>
            { LiteralsService.get('walk', true) }
          </span>
        </div>
        <div className='row-details'>
          <p className="text-bottom not-available">
            { pickedDestinationBusStop.name }
          </p>
          <p className="text-top not-available">
            ({ LiteralsService.get('notAvailable') })
          </p>
          <div>
            <button onClick={ toggleModalVisibility }>
              <img src={ images['information'] } alt="info" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const beginingStopStyles = (): CSSProperties => {
    const justifyContent = isBeginingStopUnavailable() ? 'center' : 'flex-start'
    return {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '5px',
      minHeight: '24px',
      justifyContent,
      width: '100%'
     }
  }

  const destinationStopStyles = (): CSSProperties => {
    const justifyContent = isDestinationStopUnavailable() ? 'center' : 'flex-end'
    return {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '5px',
      minHeight: '24px',
      justifyContent,
      width: '100%'
     }
  }

  const beginingStopHourStyles = (): CSSProperties => {
    const justifyContent = isBeginingStopUnavailable() ? 'center' : 'flex-start'
    return {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '5px',
      minHeight: '24px',
      justifyContent
     }
  }

  const destinationStopHourStyles = (): CSSProperties => {
    const justifyContent = isDestinationStopUnavailable() ? 'center' : 'flex-end'
    return { justifyContent, minHeight: '24px', display: 'flex', alignItems: 'flex-end' }
  }

  const exitTime = (): string => {
    // const minDateTime = moment(reservation.beginningStop.minDateTime)
    // const maxDateTime = moment(reservation.beginningStop.maxDateTime)
    // const timeDifference = moment.duration(maxDateTime.diff(minDateTime)).asMinutes() / 2
    const exitTime = reservation?.beginningStop.dateTime ?? pickedOriginBusStop?.dateTime
    return moment(exitTime).format('HH:mm') + ' h' + indicativeTimeMark(reservation?.returnAfterEventEndMinutes)
    // return aprox ? exitTime.add(timeDifference, 'm').format('HH:mm') : exitTime.format('HH:mm')
  }

  const arrivalTime = (): string => {
    // const minDateTime = moment(reservation.seats[0].exitStop.minDateTime)
    // const maxDateTime = moment(reservation.seats[0].exitStop.maxDateTime)
    // const timeDifference = moment.duration(maxDateTime.diff(minDateTime)).asMinutes() / 2
    const arrivalTime = reservation?.seats[0].exitStop.dateTime ?? pickedDestinationBusStop?.dateTime
    return moment(arrivalTime).format('HH:mm') + ' h' + indicativeTimeMark(reservation?.returnAfterEventEndMinutes)
    // return aprox ? arrivalTime.add(timeDifference, 'm').format('HH:mm') : arrivalTime.format('HH:mm')
  }

  return (
    <>
      <StyledSection>
        <div className="row-details">
          <div className="column-details">
            { stopDisabledOrigin() }
            <StopsRouteDots
              colorStart={ theme.colors.origin }
              colorEnd={ theme.colors.greenRadio }
              horizontal={ false }
            />
            { stopDisabledDestination() }
          </div>
          <div className="column-details" style={{ justifyContent: 'space-between', flex: 1 }}>
            { walkingAlternativeInStop() }
            <div style={ beginingStopStyles() }>
              <p className="text-bottom">
                { reservation?.beginningStop.name || pickedOriginBusStop?.name }
              </p>
            </div>
            <div style={ destinationStopStyles() }>
              <p className="text-bottom">
                { reservation?.seats[0].exitStop.name || pickedDestinationBusStop?.name }
              </p>
            </div>
            { walkingAlternativeOutStop() }
          </div>
          <div className="column-details" style={{ alignItems: 'flex-end', justifyContent: 'space-between' }}>
            {
              isBeginingStopUnavailable() &&
              <div className='hour'></div>
            }
            <div style={ beginingStopHourStyles() }>
              <span className="hour-text">
                { aprox && <span className='aprox-prefix'>{ `${ LiteralsService.get('aprox', true) }. ` }</span> }
                <span>
                  { exitTime() }
                </span>
              </span>
            </div>
            <div style={ destinationStopHourStyles() }>
              <span className="hour-text">
                { aprox && <span className='aprox-prefix'>{ `${ LiteralsService.get('aprox', true) }. ` }</span> }
                <span>
                  { arrivalTime() }
                </span>
              </span>
            </div>
            {
              isDestinationStopUnavailable() &&
              <div className='hour'></div>
            }
          </div>
        </div>
      </StyledSection>
      <Modal className="small" isHidden={ !isModalVisible } onOverlayClick={ toggleModalVisibility }>
        <section className='title'>
          <img src={images.information} alt='information' />
          <span className='modal-title'>
            { LiteralsService.get('stopNotAvailable', true) }
          </span>
        </section>
        <section className='content'>
          <p className='centered'>
            { LiteralsService.get('stopNotAvailableExplanation', true) }
          </p>
        </section>
        <footer>
          <Button
            title={ LiteralsService.get('understood') }
            styles={{ width: '100%' }}
            onPress={ toggleModalVisibility }
            upper
            filled
          />
        </footer>
      </Modal>
    </>
  )
}
